import { useEffect, useState } from "react";
import { FileText, Loader2, Paperclip, Plus, Save, Trash2, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { UploadProgressBar } from "@/components/ui/upload-progress-bar";
import { useDocument } from "@/hooks/useDetail";
import {
  useRemoveDocumentFile,
  useUpdateDocument,
  useUploadDocument,
  type UploadDocumentInput,
  type Visibility,
} from "@/hooks/useDocumentMutations";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { detectFileType } from "@/lib/file-detect";
import { FILE_TYPE_STYLES } from "@/lib/file-icons";
import { formatBytes } from "@/lib/format";
import type { DocumentFileRow, DocumentRow, FileType, WorkflowState } from "@/lib/types";
import { cn } from "@/lib/utils";

const FILE_TYPE_LABELS: Record<FileType, string> = {
  document: "Document",
  video:    "Video",
  audio:    "Audio",
  image:    "Image",
  other:    "Other",
};

const CATEGORY_OPTIONS = [
  { value: "policy", label: "Policy" },
  { value: "plan",   label: "Plan" },
  { value: "report", label: "Report" },
  { value: "other",  label: "Other" },
];

interface DocumentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass a row for edit mode, omit for create. */
  document?: DocumentRow | null;
  /** Initial visibility on create. Defaults to "staff"; the Private
   *  vault page passes "private" so uploads there stay private by default. */
  defaultVisibility?: Visibility;
}

/**
 * Shared dialog — POST /documents/ (create) or PATCH /documents/<id>/
 * (edit). One resource (title / description / category / visibility) can
 * bundle MANY physical files: pick or drop several, each becomes a row
 * with a remove button; they're sent together in a single multipart POST
 * under the `files` field.
 *
 * On the backend each upload becomes a DocumentFile row; the first file
 * also mirrors to Document.file for legacy single-file consumers (cards
 * + thumbnails). The detail page renders the full `document.files` list.
 */
export function DocumentFormDialog({
  open, onOpenChange, document, defaultVisibility = "staff",
}: DocumentFormDialogProps) {
  const isEdit = !!document;
  const { data: user } = useCurrentUser();
  const upload = useUploadDocument();
  const update = useUpdateDocument();
  const removeFile = useRemoveDocumentFile();
  const confirm   = useConfirm();
  const mutation = isEdit ? update : upload;

  // In edit mode, pull the full detail so we can show the existing
  // attachments (the list view only carries summary fields, not files[]).
  // `enabled` gates the request so we don't fire it for create flows.
  const detail = useDocument(isEdit && open ? document?.id : undefined);
  const existingFiles = detail.data?.files ?? [];

  const [title,       setTitle]       = useState("");
  const [description, setDescription] = useState("");
  const [fileType,    setFileType]    = useState<FileType>("document");
  const [category,    setCategory]    = useState("policy");
  const [visibility,  setVisibility]  = useState<Visibility>("staff");
  const [files,       setFiles]       = useState<File[]>([]);
  // null = no upload in flight; 0–100 = percent; -1 = length unknown.
  const [progress,    setProgress]    = useState<number | null>(null);

  // Re-classify the document's file_type from the FIRST file in the list
  // so the (single) badge on the resource card mirrors the primary file.
  useEffect(() => {
    if (files.length) setFileType(detectFileType(files[0]));
  }, [files]);

  // Re-seed on (re)open.
  useEffect(() => {
    if (!open) return;
    if (document) {
      setTitle(document.title);
      setDescription("");
      setFileType(document.file_type);
      setCategory(document.category || "policy");
      setVisibility(visibilityFromState(document.workflow_state, document.requested_visibility));
    } else {
      setTitle("");
      setDescription("");
      setFileType("document"); setCategory("policy");
      setVisibility(defaultVisibility);
    }
    setFiles([]);
    setProgress(null);
    upload.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document?.id]);

  function addFiles(picked: FileList | File[] | null) {
    if (!picked) return;
    const next = Array.from(picked);
    if (!next.length) return;
    setFiles((prev) => [...prev, ...next]);
  }

  function removePickedFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  const canSubmit =
    title.trim().length > 0
    // Create needs at least one file; edit can update metadata only.
    && (isEdit || files.length > 0)
    && !mutation.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const wantsProgress = files.length > 0;
    if (wantsProgress) setProgress(0);

    const input: Partial<UploadDocumentInput> = {
      title:       title.trim(),
      description: description.trim() || undefined,
      file_type:   fileType,
      category,
      visibility,
      files:       files.length ? files : undefined,
      onProgress:  wantsProgress ? setProgress : undefined,
    };

    const handlers = {
      onSuccess: () => onOpenChange(false),
      onSettled: () => setProgress(null),
    };

    if (isEdit && document) {
      update.mutate({ id: document.id, input }, handlers);
    } else {
      upload.mutate(input as UploadDocumentInput, handlers);
    }
  }

  const errorMsg = formatApiError(mutation.error as ApiError);

  return (
    <Dialog open={open} onOpenChange={(v) => !mutation.isPending && onOpenChange(v)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit “${document?.title}”` : "Upload resource"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update fields below. Drop new files in to add more attachments."
              : "One resource, one or many files. Drop in everything that belongs together — the PDF, the appendix, the supplementary spreadsheet."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="dfd-title">Title <span className="text-destructive">*</span></Label>
              <Input
                id="dfd-title" required maxLength={255}
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. 2026 National Adaptation Plan"
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="dfd-category">Category</Label>
              <select
                id="dfd-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={mutation.isPending}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label>
                Files {!isEdit && <span className="text-destructive">*</span>}
                {isEdit && <span className="text-xs text-muted-foreground"> — add more, or remove existing ones</span>}
              </Label>

              {/* Existing attachments (edit mode only). Each row has a
                  delete button that calls DELETE /documents/<id>/files/<id>/.
                  Detail-query refetches on success so the row vanishes. */}
              {isEdit && (
                <ExistingFilesSection
                  documentId={document!.id}
                  files={existingFiles}
                  loading={detail.isLoading}
                  onRemove={async (f) => {
                    const ok = await confirm({
                      title: `Remove "${f.original_name}"?`,
                      description: "This file will be detached from the resource. The other attachments stay.",
                      confirmLabel: "Remove file",
                      destructive: true,
                    });
                    if (!ok) return;
                    removeFile.mutate({ documentId: document!.id, fileId: f.id });
                  }}
                  removing={removeFile.isPending}
                />
              )}

              {/* Picked-but-not-yet-uploaded files. */}
              {files.length > 0 && (
                <ul className="space-y-1.5">
                  {files.map((f, i) => {
                    const t = detectFileType(f);
                    const style = FILE_TYPE_STYLES[t];
                    const Icon  = style.icon;
                    return (
                      <li
                        key={`${f.name}-${i}`}
                        className="flex items-center gap-3 rounded-md border bg-muted/30 p-2.5 text-sm"
                      >
                        <div className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                          style.chipClass,
                        )}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{f.name}</div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatBytes(f.size)}</span>
                            <span>·</span>
                            <span>{FILE_TYPE_LABELS[t]}</span>
                            {i === 0 && (
                              <>
                                <span>·</span>
                                <span className="rounded bg-primary/10 px-1.5 py-px font-medium text-primary">
                                  Primary
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button" variant="ghost" size="icon"
                          className="h-7 w-7"
                          disabled={mutation.isPending}
                          onClick={() => removePickedFile(i)}
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Always-visible add-more dropzone. Clicking opens the OS
                  picker (multi-select); dragging files over highlights it. */}
              <FileDropZone disabled={mutation.isPending} onPick={addFiles} compact={files.length > 0} />

              {progress !== null && files.length > 0 && (
                <UploadProgressBar
                  progress={progress}
                  totalBytes={files.reduce((s, f) => s + f.size, 0)}
                />
              )}
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="dfd-desc">Description</Label>
              <Textarea
                id="dfd-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={mutation.isPending}
                placeholder={isEdit ? "Leave blank to keep the existing description." : "Short summary shown in listings — covers the whole bundle."}
                rows={3}
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label>Who can see this?</Label>
              <div className="grid gap-2">
                <VisibilityOption
                  value="private" current={visibility} onChange={setVisibility} disabled={mutation.isPending}
                  label="Only me"
                  hint="Private to you. Other staff and directors won't see it."
                />
                <VisibilityOption
                  value="staff" current={visibility} onChange={setVisibility} disabled={mutation.isPending}
                  label="Staff portal"
                  hint={user?.is_director_level
                    ? "Visible to every signed-in colleague immediately."
                    : "Visible to staff after your director approves it."}
                />
                <VisibilityOption
                  value="public" current={visibility} onChange={setVisibility} disabled={mutation.isPending}
                  label="Public portal"
                  hint={
                    user?.is_ceo_level
                      ? "Live on the public site immediately."
                      : user?.is_director_level
                        ? "Goes to the CEO for final sign-off before publishing."
                        : "Goes through director, then CEO sign-off, before publishing."
                  }
                />
              </div>
            </div>
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button" variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending
                ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEdit ? "Saving…" : (
                      progress !== null && progress >= 0 && progress < 100
                        ? `Uploading ${progress}%`
                        : "Uploading…"
                    )}
                  </>
                )
                : isEdit
                  ? <><Save className="mr-2 h-4 w-4" />Save changes</>
                  : <><Upload className="mr-2 h-4 w-4" />
                      Upload {files.length > 1 ? `(${files.length} files)` : ""}</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function visibilityFromState(
  state: WorkflowState,
  requested: WorkflowState | null,
): Visibility {
  if (state === "owner_only")   return "private";
  if (state === "private_only") return "staff";
  if (state === "published")    return "public";
  const target = requested ?? "published";
  return target === "private_only" ? "staff" : "public";
}

function VisibilityOption({
  value, current, onChange, label, hint, disabled,
}: {
  value: Visibility;
  current: Visibility;
  onChange: (v: Visibility) => void;
  label: string;
  hint: string;
  disabled?: boolean;
}) {
  const checked = current === value;
  return (
    <label className={cn(
      "flex items-start gap-3 rounded-md border p-3 transition-colors",
      checked
        ? "border-primary/40 bg-primary/5"
        : "border-border hover:bg-accent/30",
      disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
    )}>
      <input
        type="radio"
        name="dfd-visibility"
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
        className="mt-1 h-4 w-4 accent-primary"
      />
      <div className="flex-1 text-sm">
        <div className="font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
    </label>
  );
}

// Shown in edit mode above the new-files picker. Lists every attachment
// currently on the resource with a small trash button. Loading state is
// just a few stubbed rows so the dialog doesn't reflow on first paint.
function ExistingFilesSection({
  documentId, files, loading, onRemove, removing,
}: {
  documentId: string;
  files: DocumentFileRow[];
  loading: boolean;
  onRemove: (f: DocumentFileRow) => void | Promise<void>;
  removing: boolean;
}) {
  void documentId;  // currently only used for analytics/log calls

  if (loading) {
    return (
      <div className="space-y-1.5">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }
  if (!files.length) {
    return (
      <div className="rounded-md border border-dashed bg-muted/20 p-3 text-xs text-muted-foreground">
        No files attached yet. Drop some in below.
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-muted-foreground">
        Currently attached ({files.length}):
      </div>
      <ul className="space-y-1.5">
        {files.map((f, i) => (
          <li
            key={f.id}
            className="flex items-center gap-3 rounded-md border bg-card p-2.5 text-sm"
          >
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{f.original_name}</span>
                {i === 0 && (
                  <span className="rounded bg-primary/10 px-1.5 py-px text-[10px] font-medium text-primary">
                    Primary
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatBytes(f.size_bytes)}
                {f.content_type ? ` · ${f.content_type}` : ""}
              </div>
            </div>
            {f.file_url && (
              <a
                href={f.file_url}
                target="_blank"
                rel="noreferrer"
                className="rounded p-1.5 text-muted-foreground transition hover:bg-accent hover:text-foreground"
                title="Open"
              >
                <Paperclip className="h-3.5 w-3.5" />
              </a>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
              disabled={removing}
              onClick={() => onRemove(f)}
              aria-label={`Remove ${f.original_name}`}
              title="Remove this file from the bundle"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FileDropZone({
  disabled, onPick, compact,
}: {
  disabled?: boolean;
  onPick: (files: FileList | File[]) => void;
  /** When the list above already has rows, render a slimmer "add more" tile. */
  compact?: boolean;
}) {
  const [drag, setDrag] = useState(false);

  return (
    <label
      htmlFor="dfd-files-input"
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (disabled) return;
        if (e.dataTransfer.files?.length) onPick(e.dataTransfer.files);
      }}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed text-sm transition-colors",
        compact ? "h-16" : "h-24",
        drag
          ? "border-primary bg-primary/5 text-primary"
          : "border-border bg-muted/20 text-muted-foreground hover:bg-muted/40",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <div className="flex items-center gap-2 font-medium">
        {compact ? <Plus className="h-4 w-4" /> : <Paperclip className="h-4 w-4" />}
        {compact ? "Add more files" : "Click or drop files"}
      </div>
      {!compact && (
        <span className="text-xs text-muted-foreground">
          Pick one or many — they'll all belong to this resource.
        </span>
      )}
      <input
        id="dfd-files-input"
        type="file"
        multiple
        disabled={disabled}
        className="sr-only"
        onChange={(e) => {
          if (e.target.files?.length) onPick(e.target.files);
          // Reset so re-picking the same file after a remove still fires.
          e.target.value = "";
        }}
      />
    </label>
  );
}

type ApiError = { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined;

function formatApiError(err: ApiError): string | null {
  if (!err?.response?.data) return null;
  const data = err.response.data as Record<string, unknown> & { detail?: string };
  if (typeof data.detail === "string") return data.detail;
  for (const [field, msgs] of Object.entries(data)) {
    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
      return `${field}: ${msgs[0]}`;
    }
  }
  return "Save failed.";
}
